import { useDebouncedValue } from '@mantine/hooks';

import { Avatar, Stack, useMantineTheme } from '@mantine/core';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import { getSummaryAndBreadCrumbs } from '@repo/common/utils/breadcrumb';
import { Text } from '@repo/foundations';
import type { MenuItem } from '@repo/secoda-editor';
import { useCallback } from 'react';
import { SecodaEntityIcon } from '../../../../SecodaEntity';
import type { MentionMenuItem } from '../hooks/useMentionMenu';
import { useMentionMenu } from '../hooks/useMentionMenu';
import type { CommandMenuProps } from './Toolbar/CommandMenu';
import { CommandMenu } from './Toolbar/CommandMenu';
import { CommandMenuItem } from './Toolbar/CommandMenuItem';

export function MentionMenu(
	props: Omit<
		CommandMenuProps<MentionMenuItem>,
		'renderMenuItem' | 'items' | 'id'
	> & {
		canMentionSecodaAi?: boolean;
	}
) {
	const { search: searchTerm, canMentionSecodaAi } = props;
	const theme = useMantineTheme();

	const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 350);

	const { isLoading, data } = useMentionMenu({
		searchTerm: debouncedSearchTerm,
		includeAiPersonas: canMentionSecodaAi,
	});

	const renderMenuItem = useCallback(
		(item: MenuItem, _index: number, options: Record<string, unknown>) => {
			const mentionItem = item as MentionMenuItem;

			const breadcrumbs = mentionItem.entity
				? getSummaryAndBreadCrumbs(
						mentionItem.entity.native_type || mentionItem.entity.entity_type,
						mentionItem.entity.search_metadata
					)
				: mentionItem.description;

			let icon: React.ReactElement;
			if (mentionItem.isDefaultAiPersona) {
				icon = <AIAvatar size={20} speed="none" />;
			} else if (mentionItem.persona) {
				icon = mentionItem.persona.avatar_url ? (
					<Avatar
						src={mentionItem.persona.avatar_url}
						size={20}
						alt={`${mentionItem.persona.name} avatar`}
					/>
				) : (
					<AIAvatar size={20} speed="none" />
				);
			} else {
				icon = <SecodaEntityIcon entity={mentionItem.entity} size={20} />;
			}

			return (
				<CommandMenuItem
					key={_index}
					icon={() => icon}
					shortcutKeys={item.shortcutKeys}
					{...options}
				>
					<Stack spacing={0} maw={theme.other.width.md}>
						<Text size="sm" lineClamp={1}>
							{mentionItem.title || 'Untitled'}
						</Text>
						<Text size="xs" color="text/secondary/default" lineClamp={1}>
							{breadcrumbs}
						</Text>
					</Stack>
				</CommandMenuItem>
			);
		},
		[theme.other.width.md]
	);

	return (
		<CommandMenu
			{...props}
			loading={isLoading}
			id="mention-menu-container"
			trigger="@"
			renderMenuItem={renderMenuItem}
			items={data ?? []}
		/>
	);
}
